import { DbEnum } from "../../../generated/prisma-enums";

/**
 * The deployment types a `sendMessage` node can dispatch to — the ONE
 * declaration read by the executor's handler registry, by the validation gate
 * that refuses an unsupported target, by the public node-type reference, and by
 * the canvas's deployment picker.
 *
 * `SendMessageNode` resolves the node's `selectedDeployment`, looks its TYPE up
 * in a handler registry, and throws when there is no handler for it. Every layer
 * upstream of that throw used to hold its own answer to "which types have a
 * handler", or no answer at all:
 *
 * - the executor's registry (`sendMessage/handlers/index.ts`) — the real one;
 * - the canvas picker (`send-message-panel-content.tsx`) — a hand-copied `Set`
 *   carrying a comment telling the next reader to keep it in step by hand;
 * - `computeConfigStatus` — checked that `selectedDeployment` was non-empty and
 *   nothing else, so an EMBED or TELEGRAM target reported `complete`;
 * - the public node-type reference — described the field as "The deployment
 *   channel ID." and named no type at all.
 *
 * So a caller who did not go through the browser (the public API,
 * `nexus workflow publish`) could create the node, validate the workflow and
 * publish it, and the FIRST statement of the limit they ever saw was the
 * runtime's `EMBED deployment is not supported yet.` — after the whole chain was
 * wired. Three use cases in one organization paid that discovery separately
 * (NEX-3961).
 *
 * Declaring the set once, here, is what lets each of those layers state the same
 * thing: the registry keys itself off this list (so a missing handler is a
 * compile error rather than a silent runtime gap), the picker filters by it, the
 * gate refuses by it, and the reference publishes it.
 *
 * 🚨 THIS IS THE RUNTIME'S CAPABILITY, NOT A POLICY. Adding a type here without
 * adding its handler makes the registry fail to compile — which is the point.
 * Removing one refuses configurations that used to run. Nothing else may narrow
 * or widen the set: a second copy is the defect this module exists to remove.
 */
export const SEND_MESSAGE_SUPPORTED_DEPLOYMENT_TYPES = [
  DbEnum.DeploymentType.WHATSAPP,
  DbEnum.DeploymentType.SLACK,
  // Email deployments carry concrete provider types since the
  // Integration→Deployment migration; both route to the shared email handler
  // ("EMAIL" is a UI category, never a stored `DeploymentType`). (NEX-2511)
  DbEnum.DeploymentType.GMAIL,
  DbEnum.DeploymentType.OUTLOOK
] as const;

/** A deployment type `sendMessage` has a handler for. */
export type SendMessageSupportedDeploymentType =
  (typeof SEND_MESSAGE_SUPPORTED_DEPLOYMENT_TYPES)[number];

/** Human-readable list for error messages and published documentation. */
export const SEND_MESSAGE_SUPPORTED_DEPLOYMENT_TYPES_LIST =
  SEND_MESSAGE_SUPPORTED_DEPLOYMENT_TYPES.join(", ");

/**
 * True when `sendMessage` can dispatch to a deployment of this type.
 *
 * Takes `unknown` because the callers hold the type at different static
 * strengths — the executor reads a `Deployment.type` off a Prisma row, while the
 * validation gate reads it out of a `Map<string, string>` built from a query —
 * and it narrows, so a caller that goes on to index the handler registry needs
 * no cast.
 */
export function isSendMessageSupportedDeploymentType(
  deploymentType: unknown
): deploymentType is SendMessageSupportedDeploymentType {
  return (
    typeof deploymentType === "string" &&
    (SEND_MESSAGE_SUPPORTED_DEPLOYMENT_TYPES as readonly string[]).includes(deploymentType)
  );
}
